import { useState } from "react";
import api, { apiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Star, ArrowLeft, ArrowRight, Crop } from "lucide-react";
import ImageCropDialog from "./ImageCropDialog";

/**
 * Galeria de fotos do produto: preview, upload múltiplo, reordenar, definir capa,
 * remover — e recortar.
 *
 * value = array de URLs; a 1ª é a capa exibida na loja. onChange recebe o novo array.
 *
 * Toda foto passa pelo recorte ANTES de subir: a que chega do fornecedor vem
 * com fundo sobrando ou com dois produtos no quadro, e sem essa parada a loja
 * mostrava a foto como veio. "Usar inteira" é um botão, para a foto que já
 * está boa não custar mais que um clique.
 *
 * Recortar uma foto já cadastrada sobe um arquivo NOVO e troca a referência:
 * o original nunca é tocado, então voltar atrás é reenviar a foto.
 */
export default function ProductImages({ value = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  /* Fila de fotos esperando recorte. Cada item: { file, src, index? } — `index`
     presente é foto já cadastrada sendo recortada (troca no lugar); ausente é
     foto nova (entra no fim). Uma de cada vez, na ordem em que foram
     escolhidas: recortar cinco fotos de uma vez seria cinco diálogos abertos. */
  const [fila, setFila] = useState([]);
  const atual = fila[0];

  const enviar = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const escolherArquivos = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setFila((f) => [...f, ...files.map((file) => ({ file, src: URL.createObjectURL(file) }))]);
  };

  /* A foto já cadastrada vem do domínio da API. `fetch` passa pelo CORS da API
     (que já libera o painel) e o blob local não "contamina" o canvas — um
     `<img src>` direto de outro domínio deixaria o recorte sem saída. */
  const recortarExistente = async (i) => {
    try {
      const res = await fetch(imgUrl(value[i]));
      const blob = await res.blob();
      const nome = value[i].split("/").pop() || "foto";
      const file = new File([blob], nome, { type: blob.type || "image/jpeg" });
      setFila((f) => [...f, { file, src: URL.createObjectURL(file), index: i }]);
    } catch {
      toast.error("Não foi possível abrir a foto para recorte.");
    }
  };

  const proximo = () => {
    if (atual) URL.revokeObjectURL(atual.src);
    setFila((f) => f.slice(1));
  };

  /* `null` = usar inteira. Foto nova sobe o original; foto já cadastrada não
     tem o que fazer. */
  const concluir = async (resultado) => {
    const item = atual;
    proximo();
    const arquivo = resultado || (item.index === undefined ? item.file : null);
    if (!arquivo) return;
    setUploading(true);
    try {
      const url = await enviar(arquivo);
      if (item.index === undefined) {
        onChange([...value, url]);
      } else {
        onChange(value.map((u, j) => (j === item.index ? url : u)));
      }
      toast.success(item.index === undefined ? "Foto enviada" : "Foto recortada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i) => onChange(value.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const makeCover = (i) => {
    if (i === 0) return;
    const next = [...value];
    const [u] = next.splice(i, 1);
    next.unshift(u);
    onChange(next);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {value.map((url, i) => (
          <div key={`${url}-${i}`} className="relative group w-24 h-24 rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid="product-image-thumb">
            {/* `object-contain`: a miniatura mostra a foto INTEIRA, senão a
                pessoa recorta, vê a miniatura cortada de novo e acha que o
                recorte não pegou. */}
            <img src={imgUrl(url)} alt="" className="w-full h-full object-contain" />
            {i === 0 && <span className="absolute top-1 left-1 text-[10px] font-semibold bg-brand-600 text-white px-1.5 py-0.5 rounded">Capa</span>}
            <button type="button" onClick={() => removeAt(i)} title="Remover" data-testid="product-image-remove"
              className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-red-500 hover:text-red-700"><X size={13} /></button>
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between bg-slate-900/45 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} title="Mover para a esquerda" className="text-white p-1 disabled:opacity-30" disabled={i === 0}><ArrowLeft size={13} /></button>
              {i !== 0 && <button type="button" onClick={() => makeCover(i)} title="Tornar capa" className="text-amber-300 p-1"><Star size={13} /></button>}
              <button type="button" onClick={() => recortarExistente(i)} title="Recortar" aria-label={`Recortar foto ${i + 1}`}
                data-testid="product-image-crop" className="text-white p-1"><Crop size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} title="Mover para a direita" className="text-white p-1 disabled:opacity-30" disabled={i === value.length - 1}><ArrowRight size={13} /></button>
            </div>
          </div>
        ))}
        <label className="w-24 h-24 flex flex-col items-center justify-center gap-1 border border-dashed border-slate-300 rounded-lg text-slate-500 cursor-pointer hover:border-brand-500 text-xs text-center px-1">
          <Upload size={18} /> {uploading ? "Enviando..." : "Adicionar"}
          <input type="file" accept="image/*" multiple onChange={escolherArquivos} aria-label="Escolher fotos do produto" className="hidden" data-testid="product-images-upload" />
        </label>
      </div>
      <p className="text-xs text-slate-400 mt-2">
        {value.length === 0
          ? "Envie uma ou mais fotos — a primeira é a capa exibida na loja. Cada foto passa por um recorte antes de subir."
          : "Passe o mouse para reordenar; ⭐ define a capa; ✂ recorta."}
        {fila.length > 1 && ` ${fila.length - 1} foto(s) na fila de recorte.`}
      </p>

      {atual && (
        <ImageCropDialog
          key={atual.src}
          src={atual.src}
          fileName={atual.file.name}
          mimeType={atual.file.type}
          onDone={concluir}
          onCancel={proximo}
        />
      )}
    </div>
  );
}
